import { useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';
import type { AiSettingsStatus } from '../../shared/ai';
import { apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Badge, Card, SectionHeading } from './ui';

export function GeminiSettingsCard() {
  const { accessToken } = useAuth();
  const [settings, setSettings] = useState<AiSettingsStatus | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('gemini-2.5-flash');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (!accessToken) return;
    let active = true;
    void apiRequest<AiSettingsStatus>('/api/ai/settings', accessToken).then(value => {
      if (active) { setSettings(value); setModel(value.model); }
    }).catch(() => { if (active) setNotice('Could not load AI settings. Reload to retry.'); });
    return () => { active = false; };
  }, [accessToken]);
  async function update(remove = false) {
    if (!accessToken || busy) return;
    setBusy(true); setNotice('');
    const suppliedKey = apiKey;
    setApiKey('');
    try {
      const value = await apiRequest<AiSettingsStatus>('/api/ai/settings', accessToken, {
        method: remove ? 'DELETE' : 'PUT',
        ...(!remove ? { body: JSON.stringify({ apiKey: suppliedKey, model }) } : {}),
      });
      setSettings(value); setModel(value.model);
      setNotice(remove ? 'Workspace key removed.' : 'Gemini connection tested. Encrypted key saved. AI drafts are ready in Inbox.');
    } catch (error) { setNotice(error instanceof Error ? error.message : 'AI settings could not be saved.'); }
    finally { setBusy(false); }
  }
  return <Card>
    <SectionHeading title="Gemini AI" description="Use your own Google AI Studio key for context-aware drafts. Every draft still requires human approval." />
    <Badge tone={settings?.configured ? 'green' : 'amber'}><KeyRound size={14} />{settings?.configured ? 'Configured' : 'Not configured'}</Badge>
    {settings?.verifiedAt && <p className="field-help">Last successful generation test: {new Date(settings.verifiedAt).toLocaleString()}</p>}
    <form onSubmit={event => { event.preventDefault(); void update(); }}>
      <label className="field"><span>{settings?.configured ? 'Replace API key' : 'Gemini API key'}</span>
        <input type="password" name="gemini-api-key" autoComplete="new-password" spellCheck={false} value={apiKey} onChange={event => setApiKey(event.target.value)} maxLength={4096} disabled={busy || !settings?.storageAvailable} placeholder="Paste your key here" />
      </label>
      <label className="field"><span>Gemini model</span><input value={model} onChange={event => setModel(event.target.value)} maxLength={87} disabled={busy} required /></label>
      <p className="field-help">The key is encrypted on the server and never shown again. Testing sends synthetic text to Google and may use a small amount of your API quota. Drafting sends the selected business context and message to Google.</p>
      <p className="field-help"><a href="https://aistudio.google.com/api-keys" target="_blank" rel="noreferrer">Open Google AI Studio</a> to create a key and check quota.</p>
      <div className="button-row">
        <button className="button button--primary" disabled={busy || !settings?.storageAvailable || apiKey.trim().length < 16}>{busy ? 'Working…' : 'Test and save key'}</button>
        {settings?.source === 'workspace' && <button className="button button--secondary" type="button" disabled={busy} onClick={() => void update(true)}>Remove saved key</button>}
      </div>
    </form>
    <p role="status" aria-live="polite">{notice}</p>
  </Card>;
}
